import { useState, useEffect, useCallback } from 'react'

interface VisualViewportState {
  height: number
  keyboardHeight: number
  isKeyboardOpen: boolean
}

/**
 * Hook to track the visual viewport height and detect mobile keyboard presence.
 * Uses the visualViewport API to detect when the keyboard opens/closes.
 * Includes iOS Safari specific handling.
 */
export function useVisualViewport(): VisualViewportState {
  const [state, setState] = useState<VisualViewportState>(() => {
    const height = window.visualViewport?.height ?? window.innerHeight
    return {
      height,
      keyboardHeight: 0,
      isKeyboardOpen: false,
    }
  })

  const updateViewport = useCallback(() => {
    const viewport = window.visualViewport
    if (!viewport) return

    // Get the full window height (without keyboard)
    const fullHeight = window.innerHeight
    // Get current visible height
    const visibleHeight = viewport.height

    // Calculate keyboard height - on iOS, the viewport shrinks when keyboard opens
    // Also account for viewport offset (iOS scrolls the viewport up)
    const heightDiff = fullHeight - visibleHeight

    // Keyboard is considered open if viewport shrinks by more than 100px
    // or if there's a significant offset (iOS behavior)
    const isKeyboardOpen = heightDiff > 100 || viewport.offsetTop > 50

    setState({
      height: visibleHeight,
      keyboardHeight: isKeyboardOpen ? Math.max(heightDiff, 0) : 0,
      isKeyboardOpen,
    })
  }, [])

  useEffect(() => {
    const viewport = window.visualViewport

    if (!viewport) {
      // Fallback for browsers without visualViewport API
      // Use focus/blur events as a heuristic
      const handleFocus = (e: FocusEvent) => {
        const target = e.target as HTMLElement
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
          // Assume keyboard is open when input is focused
          setTimeout(() => {
            setState(prev => ({
              ...prev,
              isKeyboardOpen: true,
              keyboardHeight: 300, // Estimate
              height: window.innerHeight - 300,
            }))
          }, 300)
        }
      }

      const handleBlur = () => {
        setState({
          height: window.innerHeight,
          keyboardHeight: 0,
          isKeyboardOpen: false,
        })
      }

      document.addEventListener('focusin', handleFocus)
      document.addEventListener('focusout', handleBlur)

      return () => {
        document.removeEventListener('focusin', handleFocus)
        document.removeEventListener('focusout', handleBlur)
      }
    }

    // Use visualViewport API
    viewport.addEventListener('resize', updateViewport)
    viewport.addEventListener('scroll', updateViewport)

    // Also listen for window resize (orientation changes)
    window.addEventListener('resize', updateViewport)

    // Initial check
    updateViewport()

    return () => {
      viewport.removeEventListener('resize', updateViewport)
      viewport.removeEventListener('scroll', updateViewport)
      window.removeEventListener('resize', updateViewport)
    }
  }, [updateViewport])

  return state
}
